import * as React from 'react';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Form, Formik } from 'formik';
import useProductName from 'brand-customization/useProductName';

import { Button, Col, Modal, Row } from 'components/bootstrap';
import FormikInput from 'components/common/FormikInput';
import Spinner from 'components/common/Spinner';
import { InputDescription, ModalSubmit, IfPermitted } from 'components/common';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import type { AIConfigurationType } from 'ai/hooks/useAIConfiguration';
import useAIConfiguration from 'ai/hooks/useAIConfiguration';
import useEnableAIReportMutation from 'security-app/components/Investigations/AIReport/hooks/useEnableAIReportMutation';

const LabelSpan = styled.span(
  ({ theme }) => css`
    margin-left: ${theme.spacings.sm};
    font-weight: bold;
  `,
);

const AIReportConfiguration = () => {
  const { data, isLoading } = useAIConfiguration();
  const [showModal, setShowModal] = useState<boolean>(false);
  const { enable, disable } = useEnableAIReportMutation();
  const productName = useProductName();

  const sendTelemetry = useSendTelemetry();

  const _saveConfig = (values: AIConfigurationType) => {
    const action = values.isEnabled ? enable : disable;

    action().then(() => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.AI_REPORT_CONFIGURATION_SAVED, {
        app_pathname: 'configurations',
        app_section: 'investigation-ai-report',
      });

      setShowModal(false);
    });
  };

  const _resetConfig = () => {
    setShowModal(false);
  };

  const title = 'AI Feature Services';
  const modalTitle = `Configure ${title}`;

  return (
    <div>
      <h2>{title}</h2>
      <p>
        Manage access to AI-powered capabilities within {productName}. Enable or disable AI-driven features for your
        environment.
      </p>

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <dl className="deflist">
            <dt>Status:</dt>
            <dd>{data.isEnabled ? 'Enabled' : 'Disabled'}</dd>
          </dl>

          <IfPermitted permissions="*">
            <p>
              <Button
                type="button"
                bsSize="xs"
                bsStyle="info"
                onClick={() => {
                  setShowModal(true);
                }}>
                Edit configuration
              </Button>
            </p>
          </IfPermitted>

          <Modal show={showModal} onHide={_resetConfig}>
            <Formik onSubmit={_saveConfig} initialValues={data}>
              {({ isSubmitting }) => (
                <Form>
                  <Modal.Header>
                    <Modal.Title>{modalTitle}</Modal.Title>
                  </Modal.Header>

                  <Modal.Body>
                    <div>
                      <Row>
                        <Col sm={12}>
                          <FormikInput
                            type="checkbox"
                            name="isEnabled"
                            id="isEnabled"
                            label={<LabelSpan>Enabled</LabelSpan>}
                          />
                          <InputDescription help={<>Enable or disable AI-driven features for your environment.</>} />
                        </Col>
                      </Row>
                    </div>
                  </Modal.Body>

                  <Modal.Footer>
                    <ModalSubmit
                      onCancel={_resetConfig}
                      isSubmitting={isSubmitting}
                      submitButtonText="Update configuration"
                      submitLoadingText="Updating configuration..."
                    />
                  </Modal.Footer>
                </Form>
              )}
            </Formik>
          </Modal>
        </>
      )}
    </div>
  );
};

export default AIReportConfiguration;
